"use client";

/**
 * ToolRenderer - Main dispatcher for tool displays
 *
 * Receives an aggregated tool call and renders the appropriate display component.
 * Handles:
 * - Registry lookup for display components
 * - Lazy loading of specialized displays
 * - Error boundary wrapping
 * - Approval states for human-in-the-loop tools
 */

import { Suspense, createElement, lazy, useMemo, useState, ComponentType } from "react";
import { motion } from "framer-motion";
import { Spinner } from "@/ui";
import { spacing } from "@/ui/tokens";
import { ToolErrorBoundary } from "./ToolErrorBoundary";
import { getToolDisplay, DefaultDisplay, shouldAlwaysShowRich } from "./ToolDisplays";
import { ApprovalToolDisplay, ApprovalRespondedDisplay, ToolThinkingDisplay } from "./ToolDisplays/generic";
import type { AggregatedToolCall, ToolDisplayProps } from "@/lib/sola/types/tools";
import { requiresApproval } from "@/lib/sola/types/tools";
import { useChatStore, selectDebugMode } from "@/lib/sola/store";

/**
 * Approval handler callback type
 */
export type ApprovalHandler = (
  toolCallId: string,
  toolName: string,
  approved: boolean
) => Promise<void>;

export interface ToolRendererProps {
  toolCall: AggregatedToolCall;
  isStreaming: boolean;
  /** Handler for approval responses (required for human-in-the-loop tools) */
  onApprovalResponse?: ApprovalHandler;
}

/**
 * Loading placeholder for lazy-loaded components
 */
function LoadingFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: spacing[8],
        paddingBottom: spacing[8],
      }}
    >
      <Spinner size="sm" />
    </div>
  );
}

/**
 * Create a lazy component wrapper for dynamic imports
 */
function createLazyComponent(
  importFn: () => Promise<{ default: ComponentType<any> }>
): ComponentType<ToolDisplayProps> {
  return lazy(importFn);
}

/**
 * ToolRenderer component
 *
 * Dispatches to the appropriate display component based on tool name.
 */
export function ToolRenderer({
  toolCall,
  isStreaming,
  onApprovalResponse,
}: ToolRendererProps) {
  const { toolName, state } = toolCall;
  const [isApprovalProcessing, setIsApprovalProcessing] = useState(false);
  const debugMode = useChatStore(selectDebugMode);

  // Get the display component from registry
  const { component: DisplayComponent, lazy: isLazy } = useMemo(
    () => getToolDisplay(toolName),
    [toolName]
  );

  // Build the lazy wrapper once per import function rather than on every render.
  // Calling lazy() inline during render creates a fresh component each time,
  // which remounts the subtree and trips react-hooks/static-components.
  const LazyComponent = useMemo(
    () =>
      isLazy
        ? createLazyComponent(
            DisplayComponent as () => Promise<{ default: ComponentType<any> }>
          )
        : null,
    [isLazy, DisplayComponent]
  );

  // Check if this is an approval state
  const isApprovalRequested = state === "approval-requested";
  const isApprovalResponded = state === "approval-responded";
  const needsApprovalUI = requiresApproval(toolName);

  // Handle approval response
  const handleApprove = async () => {
    if (!onApprovalResponse) {
      console.warn("[ToolRenderer] No approval handler provided");
      return;
    }
    setIsApprovalProcessing(true);
    try {
      await onApprovalResponse(toolCall.toolCallId, toolName, true);
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!onApprovalResponse) {
      console.warn("[ToolRenderer] No approval handler provided");
      return;
    }
    setIsApprovalProcessing(true);
    try {
      await onApprovalResponse(toolCall.toolCallId, toolName, false);
    } finally {
      setIsApprovalProcessing(false);
    }
  };

  // For approval-requested state, show approval UI
  if (isApprovalRequested && needsApprovalUI) {
    return (
      <ToolErrorBoundary toolName={toolName}>
        <ApprovalToolDisplay
          toolCall={toolCall}
          onApprove={handleApprove}
          onDeny={handleDeny}
          isProcessing={isApprovalProcessing}
        />
      </ToolErrorBoundary>
    );
  }

  // For approval-responded state, show outcome
  if (isApprovalResponded && needsApprovalUI) {
    const approved = toolCall.approval?.approved ?? false;
    return (
      <ToolErrorBoundary toolName={toolName}>
        <ApprovalRespondedDisplay toolCall={toolCall} approved={approved} />
      </ToolErrorBoundary>
    );
  }

  // Thinking mode: when debug mode is OFF, show compact summary instead of full display
  if (!debugMode && !shouldAlwaysShowRich(toolName)) {
    return <ToolThinkingDisplay toolCall={toolCall} />;
  }

  // Create props for the display component
  const displayProps: ToolDisplayProps = {
    toolCall,
    isStreaming,
  };

  // Render the display component
  // For lazy components, wrap in Suspense
  if (isLazy && LazyComponent) {
    return (
      <ToolErrorBoundary toolName={toolName}>
        <Suspense fallback={<LoadingFallback />}>
          {createElement(LazyComponent, displayProps)}
        </Suspense>
      </ToolErrorBoundary>
    );
  }

  // For sync components, render directly
  const SyncComponent = DisplayComponent as ComponentType<ToolDisplayProps>;

  return (
    <ToolErrorBoundary toolName={toolName}>
      <SyncComponent {...displayProps} />
    </ToolErrorBoundary>
  );
}

/**
 * Render multiple tool calls
 */
export function ToolRendererList({
  toolCalls,
  isStreaming,
  onApprovalResponse,
}: {
  toolCalls: AggregatedToolCall[];
  isStreaming: boolean;
  onApprovalResponse?: ApprovalHandler;
}) {
  const debugMode = useChatStore(selectDebugMode);

  if (toolCalls.length === 0) return null;

  // Tighter spacing and faster animation in thinking mode
  const gap = debugMode ? spacing[4] : spacing[1];
  const yOffset = debugMode ? 8 : 3;
  const duration = debugMode ? 0.3 : 0.15;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {toolCalls.map((toolCall, index) => (
        <motion.div
          key={toolCall.toolCallId}
          initial={{ opacity: 0, y: yOffset }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration, delay: index * 0.05 }}
        >
          <ToolRenderer
            toolCall={toolCall}
            isStreaming={isStreaming}
            onApprovalResponse={onApprovalResponse}
          />
        </motion.div>
      ))}
    </div>
  );
}

export default ToolRenderer;
